import { createSlice } from '@reduxjs/toolkit';
import {
  getAllPropertyDetails,
  getAllAccommodationsDetails,
  getAllAmenitiyDetails,
  getAllRestaurantDetails,
  getUserInviteSelectedRestaurants,
  getAllSpaDetails,
  getAllLocations,
  getAllPropertyConfigs,
} from '../../utils/PropertyAPIUtils';
import { sortBy } from 'lodash';
import {
  getFlows,
  getUiBuilderPages,
  getUIBuilderHotelModuleOptions,
  getUiBuilderMenuItems,
  getUiBuilderHamburgerMenu,
} from '../../utils/UiBuilderAPIUtils';
import { getRestaurantTiming } from '../../utils/dashboardUtils';
import { setTimings } from '../home/DashboardSlice';
import { decryptLocalStorage } from '../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../utils/loaderSlice';
import { logError } from '../../utils/commonUtils';
import { ENVIRONMENT } from '../../env/Configurations';

export const propertySlice = createSlice({
  name: 'property',
  initialState: {
    allGroups: [],
    allBrands: [],
    allHotels: [],
    allCategories: [],
    allSections: [],
    allRoomTypes: [],
    allRooms: [],
    allPackages: [],
    allFloors: [],
    allUpgrades: [],
    allPersonalisations: [],
    allRateFilters: [],
    allPreferences: [],
    allAmenityCategories: [],
    allAmenitySections: [],
    allAmenities: [],
    allLocations: [],
    filteredGroups: [],
    filteredBrands: [],
    filteredHotels: [],
    allSpaInfo: [],
    allSpaCategories: [],
    allSpaTreatments: [],
    selectedGroup: '',
    selectedBrand: '',
    selectedHotel: '',
    selectedHotelCurrency: '',
    selectedCurrencySymbol: '',
    selectedHotelTimeZone: '',
    allRestaurantLoader: false,
    allRestaurant: [],
    allRestaurantChef: [],
    allUIBuilderPages: [],
    allUIBuilderFlows: [],
    allUIBuilderHotelModuleOptions: [],
    uiBuilderMenuLoader: false,
    uiBuilderMenu: [],
    uiBuilderHamburgerMenu: [],
    dashboardSelectedSpa: '',
    dashboardSelectedRestaurant: '',
    menuConfigSelectedRestaurant: '',
    allProperties: {},
    Pagination: null,
    userInviteSelectedRestaurant: [],
    isUiBuilderEditMode: false,
    allPropertyConfigs: {},
    activeGroup: {},
    allLovDetails: [],
    lovLoader: false,
    lovError: null,
    primaryCuisineDetails: [],
    secondaryCuisineDetails: [],
  },
  reducers: {
    setAllGroups: (state, action) => {
      state.allGroups = action.payload;
      state.filteredGroups = action.payload;
      const groupId =
        state.selectedGroup &&
        action.payload.findIndex((group) => group.id === state.selectedGroup) > -1
          ? state.selectedGroup
          : '';
      state.activeGroup = state.allGroups.find((group) => group.id === groupId);
      propertySlice.caseReducers.setSelectedGroup(state, { groupId });
    },
    setAllBrands: (state, action) => {
      state.allBrands = action.payload;
      state.filteredBrands = state.selectedGroup
        ? action.payload.filter((brand) => brand.groupId === state.selectedGroup)
        : action.payload;

      const brandId =
        state.selectedBrand &&
        action.payload.findIndex((brand) => brand.id === state.selectedBrand) > -1
          ? state.selectedBrand
          : '';
      propertySlice.caseReducers.setSelectedBrand(state, { brandId });
    },
    setAllHotels: (state, action) => {
      state.allHotels = action.payload;

      if (state.selectedBrand) {
        state.filteredHotels = action.payload.filter(
          (hotel) => hotel.brandId === state.selectedBrand,
        );
      } else {
        if (state.selectedGroup) {
          state.filteredHotels = action.payload.filter(
            (hotel) => hotel.groupId === state.selectedGroup,
          );
        } else {
          state.filteredHotels = action.payload;
        }
      }

      const hotelId =
        state.selectedHotel &&
        action.payload.findIndex((hotel) => hotel.id === state.selectedHotel) > -1
          ? state.selectedHotel
          : '';
      propertySlice.caseReducers.setSelectedHotel(state, { hotelId });
    },
    setSelectedGroup: (state, action) => {
      const id = action.groupId !== undefined ? action.groupId : action.payload;
      state.selectedGroup = id;
      state.activeGroup = state.allGroups.find((group) => group.id === id);
      if (id) {
        state.filteredBrands = state.allBrands.filter((brand) => brand.groupId === id);
        if (state.filteredBrands.length === 0) {
          state.filteredHotels = [];
        } else {
          state.filteredHotels = state.allHotels.filter((hotel) => hotel.groupId === id);
        }
      } else {
        state.filteredBrands = state.allBrands;
        state.filteredHotels = state.allHotels;
      }
    },
    setSelectedBrand: (state, action) => {
      const id = action.brandId !== undefined ? action.brandId : action.payload;
      state.selectedBrand = id;

      if (state.selectedBrand) {
        state.filteredHotels = state.allHotels.filter((hotel) => hotel.brandId === id);
      } else {
        if (state.selectedGroup) {
          state.filteredHotels = state.allHotels.filter(
            (hotel) => hotel.groupId === state.selectedGroup,
          );
        } else {
          state.filteredHotels = state.allHotels;
        }
      }
    },
    setSelectedHotel: (state, action) => {
      const id = action.hotelId !== undefined ? action.hotelId : action.payload;
      state.selectedHotel = id;
      const hotelCurrency =
        state.filteredHotels.find((hotel) => hotel.id === state.selectedHotel)?.currency || '';
      propertySlice.caseReducers.setSelectedHotelCurrency(state, {
        hotelCurrency,
      });

      const hotelTimeZone =
        state.filteredHotels.find((hotel) => hotel.id === state.selectedHotel)?.location
          ?.timezone || '';
      propertySlice.caseReducers.setSelectedHotelTimeZone(state, {
        hotelTimeZone,
      });
    },

    setSelectedHotelCurrency: (state, action) => {
      state.selectedHotelCurrency =
        action.hotelCurrency !== undefined ? action.hotelCurrency : action.payload;
    },

    setSelectedHotelTimeZone: (state, action) => {
      state.selectedHotelTimeZone =
        action.hotelTimeZone !== undefined ? action.hotelTimeZone : action.payload;
    },
    setAllCategories: (state, action) => {
      state.allCategories = action.payload;
    },

    setAllRoomTypes: (state, action) => {
      state.allRoomTypes = action.payload;
    },

    setAllPackages: (state, action) => {
      state.allPackages = action.payload;
    },

    setAllFloors: (state, action) => {
      state.allFloors = action.payload;
    },

    setAllRooms: (state, action) => {
      state.allRooms = action.payload;
    },

    setAllUpgrades: (state, action) => {
      state.allUpgrades = action.payload;
    },

    setAllPersonalisations: (state, action) => {
      state.allPersonalisations = action.payload;
    },

    setAllRateFilters: (state, action) => {
      state.allRateFilters = action.payload;
    },

    setAllPreferences:(state, action) => {
      state.allPreferences = action.payload;
    },

    setAllAmenitySections: (state, action) => {
      state.allAmenitySections = action.payload;
    },
    setAllAmenityCategories: (state, action) => {
      state.allAmenityCategories = action.payload;
    },
    setAllAmenities: (state, action) => {
      state.allAmenities = action.payload;
    },
    setAllLocations: (state, action) => {
      state.allLocations = action.payload;
    },
    initRestaurantState: (state, action) => {
      state.allRestaurant = [];
      state.allRestaurantChef = [];
      state.allRestaurantLoader = true;
      state.allRestaurantError = null;
    },
    setAllRestaurant: (state, action) => {
      state.allRestaurant = action.payload;
      state.allRestaurantLoader = false;
      state.allRestaurantError = null;
    },
    setAllRestaurantError: (state, action) => {
      state.allRestaurantError = action.payload;
      state.allRestaurantLoader = false;
      state.allRestaurant = [];
    },
    setAllRestaurantChef: (state, action) => {
      state.allRestaurantChef = action.payload;
      state.allRestaurantChefError = null;
      state.allRestaurantLoader = false;
    },
    setAllRestaurantChefError: (state, action) => {
      state.allRestaurantChef = [];
      state.allRestaurantChefError = action.payload;
      state.allRestaurantLoader = false;
    },

    setAllSpaInfo: (state, action) => {
      state.allSpaInfo = action.payload;
    },
    setAllSpaCategories: (state, action) => {
      state.allSpaCategory = action.payload;
    },
    setAllSpaTreatments: (state, action) => {
      state.allSpaTreatments = action.payload;
    },
    setAllUiBuilderPages: (state, action) => {
      state.allUIBuilderPages = action.payload;
    },
    setAllUIBuilderFlows: (state, action) => {
      state.allUIBuilderFlows = action.payload;
    },
    setAllUIBuilderHotelModuleOptions: (state, action) => {
      state.allUIBuilderHotelModuleOptions = action.payload;
    },
    allUIBuilderMenu: (state, action) => {
      state.uiBuilderMenuLoader = true;
      state.uiBuilderMenu = [];
    },
    setAllUIBuilderMenu: (state, action) => {
      state.uiBuilderMenuLoader = false;
      state.uiBuilderMenu = action.payload;
    },
    setAllUIBuilderMenuError: (state, action) => {
      state.uiBuilderMenuLoader = false;
      state.uiBuilderMenu = [];
    },
    setAllUIBuilderHamburgerMenu: (state, action) => {
      state.uiBuilderHamburgerMenu = action.payload;
    },
    setDashboardSelectedSpa: (state, action) => {
      state.dashboardSelectedSpa = action.payload;
    },
    setDashboardSelectedRestaurant: (state, action) => {
      state.dashboardSelectedRestaurant = action.payload;
    },
    setMenuConfigSelectedRestaurant: (state, action) => {
      state.menuConfigSelectedRestaurant = action.payload;
    },
    setAllProperties: (state, action) => {
      state.allProperties = action.payload;
    },
    setPagination: (state, action) => {
      state.Pagination = action.payload;
    },
    setUserInviteSelectedRestaurant: (state, action) => {
      state.userInviteSelectedRestaurant = action.payload;
    },
    initUserInviteSelectedRestaurant: (state, action) => {
      state.userInviteSelectedRestaurant = [];
    },
    setIsUiBuilderEditModeStatus: (state, action) => {
      state.isUiBuilderEditMode = action.payload;
    },
    setAllPropertyConfigs: (state, action) => {
      state.allPropertyConfigs = action.payload;
    },
    initLovState: (state) => {
      state.allLovDetails = [];
      state.lovLoader = true;
      state.lovError = null;
    },
    setAllLovDetails: (state, action) => {
      state.allLovDetails = action.payload;
      state.lovLoader = false;
      state.lovError = null;
    },
    setLovError: (state, action) => {
      state.lovError = action.payload;
      state.lovLoader = false;
      state.allLovDetails = [];
    },

    setPrimaryCuisineDetails: (state, action) => {
      state.primaryCuisineDetails = action.payload;
    },
    setSecondaryCuisineDetails: (state, action) => {
      state.secondaryCuisineDetails = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setSelectedGroup,
  setSelectedBrand,
  setSelectedHotel,
  setSelectedHotelCurrency,
  setAllGroups,
  setAllBrands,
  setAllHotels,
  setAllCategories,
  setAllRoomTypes,
  setAllPackages,
  setAllRooms,
  setAllFloors,
  setAllUpgrades,
  setAllPersonalisations,
  setAllRateFilters,
  setAllPreferences,
  setAllLocations,
  setAllAmenitySections,
  setAllAmenities,
  setAllAmenityCategories,
  initRestaurantState,
  setAllRestaurant,
  setAllRestaurantError,
  setAllRestaurantChef,
  setAllRestaurantChefError,
  setAllSpaInfo,
  setAllSpaCategories,
  setAllSpaTreatments,
  setAllUiBuilderPages,
  setAllUIBuilderFlows,
  setAllUIBuilderHotelModuleOptions,
  allUIBuilderMenu,
  setAllUIBuilderMenu,
  setAllUIBuilderHamburgerMenu,
  setAllUIBuilderMenuError,
  setDashboardSelectedSpa,
  setDashboardSelectedRestaurant,
  setMenuConfigSelectedRestaurant,
  setAllProperties,
  setPagination,
  setUserInviteSelectedRestaurant,
  initUserInviteSelectedRestaurant,
  setIsUiBuilderEditModeStatus,
  setAllPropertyConfigs,
  setAllLovDetails,
  initLovState,
  setLovError,
  setPrimaryCuisineDetails,
  setSecondaryCuisineDetails,
} = propertySlice.actions;

// ------------------------------------------------------------------------------------------------------------------------ //
// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(setAllGroups([{}, {}]))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched

// Fetch all property details and set to global store
export const fetchAllProperties = () => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    const res = await getAllPropertyDetails(getByLang);
    dispatch(
      setAllGroups(
        sortBy(res?.data?.getPropertyDetails?.groups, [(group) => group?.name?.toLowerCase()]),
      ),
    );
    dispatch(
      setAllBrands(
        sortBy(res?.data?.getPropertyDetails?.brands, [(brand) => brand?.name?.toLowerCase()]),
      ),
    );
    dispatch(
      setAllHotels(
        sortBy(res?.data?.getPropertyDetails?.hotels, [(hotel) => hotel?.name?.toLowerCase()]),
      ),
    );
  } catch (error) {
    logError(null, 'fetchAllProperties', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// Fetch all amenities related details and set to global store
export const fetchAllAmenities = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    const res = await getAllAmenitiyDetails(hotelId, language);
    const sections = res?.data?.getHotelAmenityDetails?.section;
    dispatch(setAllAmenitySections(sections));
    dispatch(setAllAmenityCategories(res?.data?.getHotelAmenityDetails?.categories));
    dispatch(setAllAmenities(res?.data?.getHotelAmenityDetails?.amenities));
  } catch (error) {
    logError(hotelId, 'fetchAllAmenities', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllLocations = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    const res = await getAllLocations(hotelId, language);
    dispatch(setAllLocations(res?.data?.getLocations || []));
  } catch (error) {
    logError(hotelId, 'fetchAllLocations', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};
// Fetch all accommodation related details and set to global store
export const fetchAllAccommodations = (hotelId) => async (dispatch) => {
  const lang = decryptLocalStorage('lang');
  const getByLang = lang !== 'en' ? lang : '';
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAllAccommodationsDetails(hotelId, getByLang);
    dispatch(setAllCategories(res?.data?.getHotelAccommodationDetails?.categories));
    dispatch(setAllRoomTypes(res?.data?.getHotelAccommodationDetails?.roomTypes));
    dispatch(setAllRooms(res?.data?.getHotelAccommodationDetails?.rooms));
    dispatch(setAllPackages(res?.data?.getHotelAccommodationDetails?.packages));
    dispatch(setAllFloors(res?.data?.getHotelAccommodationDetails?.floors));
    dispatch(setAllUpgrades(res?.data?.getHotelAccommodationDetails?.upgrades)); // Show unsorted default list
    dispatch(setAllRateFilters(res?.data?.getHotelAccommodationDetails?.rateFilters));
    dispatch(setAllPersonalisations(res?.data?.getHotelAccommodationDetails?.personalisations));
    dispatch(setAllPreferences(res?.data?.getHotelAccommodationDetails?.preferences));
  } catch (error) {
    logError(hotelId, 'fetchAllAccommodations', error?.message);
    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// Fetch all restaurant related details and set to global store
export const fetchAllRestaurant = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(initRestaurantState());
    const res = await getAllRestaurantDetails(hotelId, getByLang);
    const sortedRestList = [...res?.data?.getRestaurantDetails?.restaurant];
    dispatch(setAllRestaurant(sortedRestList));

    if (sortedRestList.length > 0) {
      dispatch(setDashboardSelectedRestaurant(sortedRestList[0]));
      const restaurantTiming = getRestaurantTiming(new Date(), sortedRestList[0]);
      dispatch(setTimings(restaurantTiming));
    }

    let chefList = [];
    chefList = res.data.getRestaurantDetails.chef.map((chefKey) => {
      const restaurantName = res?.data?.getRestaurantDetails?.restaurant?.find((restroData) =>
        restroData?.id === chefKey?.restaurantId ? restroData?.name : null,
      );
      return {
        ...chefKey,
        restaurantName: restaurantName ? restaurantName?.name : null,
      };
    });
    dispatch(setAllRestaurantChef(chefList));
  } catch (error) {
    dispatch(setAllRestaurantError());
    dispatch(setAllRestaurantChefError());
    logError(hotelId, 'fetchAllRestaurant', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// Fetch all the restaurant related details by hotel id's which are select in user role invite Culinary Operations Manager
export const fetchAllRestaurantByHotelId = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(initUserInviteSelectedRestaurant());
    const res = await getUserInviteSelectedRestaurants(hotelId, getByLang);
    const sortedRestList = [...res?.data?.getRestaurantsByHotelIds];
    dispatch(setUserInviteSelectedRestaurant(sortedRestList));
  } catch (error) {
    logError(hotelId, 'fetchAllRestaurantByHotelId', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }

    return false;
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// Fetch all spa related details and set to global store
export const fetchAllSpa = (hotelId) => async (dispatch) => {
  const lang = decryptLocalStorage('lang');
  const getByLang = lang !== 'en' ? lang : '';
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAllSpaDetails(hotelId, getByLang);
    const sortedSpaList = res?.data?.getSpaDetails['spa'];
    dispatch(setAllSpaInfo(sortedSpaList));

    if (sortedSpaList.length > 0) {
      dispatch(setDashboardSelectedSpa(sortedSpaList[0]));
    } else {
      dispatch(setDashboardSelectedSpa(null));
    }
    dispatch(setAllSpaCategories(res?.data?.getSpaDetails?.categories));
    dispatch(setAllSpaTreatments(res?.data?.getSpaDetails?.treatments));
  } catch (error) {
    logError(hotelId, 'fetchAllSpa', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// fetch UI builder related details to global store
export const fetchAllUIBuilderPages = (hotelId) => async (dispatch) => {
  const lang = decryptLocalStorage('lang');
  const getByLang = lang !== 'en' ? lang : '';
  try {
    dispatch(stepUpLoaderCount());
    const res = await getUiBuilderPages(hotelId, getByLang);
    dispatch(
      setAllUiBuilderPages(
        sortBy(res?.data?.listUiBuilderPages, [(page) => page?.name?.toLowerCase()]),
      ),
    );
  } catch (error) {
    logError(hotelId, 'fetchAllUIBuilderPages', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllUIBuilderFlows = () => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    const res = await getFlows(getByLang);
    dispatch(
      setAllUIBuilderFlows(sortBy(res?.data?.listFlowOptions, [(flow) => flow?.id?.toLowerCase()])),
    );
  } catch (error) {
    logError(null, 'fetchAllUIBuilderFlows', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllUIBuilderHotelModuleOptions = () => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    const res = await getUIBuilderHotelModuleOptions(getByLang);
    dispatch(
      setAllUIBuilderHotelModuleOptions(
        sortBy(res?.data?.listHotelModuleOptions, [(flow) => flow?.label?.toLowerCase()]),
      ),
    );
  } catch (error) {
    logError(null, 'fetchAllUIBuilderHotelModuleOptions', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllUIBuilderMenu = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    dispatch(allUIBuilderMenu());
    const res = await getUiBuilderMenuItems(hotelId, getByLang);
    dispatch(setAllUIBuilderMenu(res?.data?.listUiBuilderMenuItems?.menuList));
  } catch (error) {
    dispatch(setAllUIBuilderMenuError());
    logError(hotelId, 'fetchAllUIBuilderMenu', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllUIBuilderHamburgerMenu = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const lang = decryptLocalStorage('lang');
    const getByLang = lang !== 'en' ? lang : '';
    const res = await getUiBuilderHamburgerMenu(hotelId, getByLang);
    dispatch(setAllUIBuilderHamburgerMenu(res?.data?.getUiBuilderHamburgerMenuDetails));
  } catch (error) {
    logError(hotelId, 'fetchAllUIBuilderHamburgerMenu', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

export const fetchAllPropertyConfigData = (hotelId) => async (dispatch) => {
  try {
    dispatch(stepUpLoaderCount());
    const res = await getAllPropertyConfigs(hotelId);
    dispatch(setAllPropertyConfigs(res?.data?.getSyncConfigs));
  } catch (error) {
    logError(hotelId, 'fetchAllPropertyConfigData', error?.message);

    if (ENVIRONMENT === 'dev') {
      console.log(error?.message);
    }
  } finally {
    dispatch(stepDownLoaderCount());
  }
};

// ------------------------------------------------------------------------------------------------------------------------ //

export const allGroupsSelector = (state) => state.property.allGroups;
export const allBrandsSelector = (state) => state.property.allBrands;
export const allHotelsSelector = (state) => state.property.allHotels;
export const allCategoriesSelector = (state) => state.property.allCategories;
export const allRoomTypesSelector = (state) => state.property.allRoomTypes;
export const allPackagesSelector = (state) => state.property.allPackages;
export const allFloorsSelector = (state) => state.property.allFloors;
export const allRoomsSelector = (state) => state.property.allRooms;
export const allRateFiltersSelector = (state) => state.property.allRateFilters;
export const allPersonalisationsSelector = (state) => state.property.allPersonalisations;
export const allPreferencesSelector = (state) => state.property.allPreferences;
export const allUpgradesSelector = (state) => state.property.allUpgrades;
export const allAmenitiesSelector = (state) => state.property.allAmenities;
export const allLocationsSelector = (state) => state.property.allLocations;
export const allAmenityCategoriesSelector = (state) => state.property.allAmenityCategories;
export const allAmenitySectionsSelector = (state) => state.property.allAmenitySections;
export const filteredGroupsSelector = (state) => state.property.filteredGroups;
export const filteredBrandsSelector = (state) => state.property.filteredBrands;
export const filteredHotelsSelector = (state) => state.property.filteredHotels;
export const selectedGroupSelector = (state) => state.property.selectedGroup;
export const selectedBrandSelector = (state) => state.property.selectedBrand;
export const selectedHotelSelector = (state) => state.property.selectedHotel;
export const selectedHotelCurrencySelector = (state) => state.property.selectedHotelCurrency;
export const selectedHotelTimeZoneSelector = (state) => state.property.selectedHotelTimeZone;
export const allRestaurantLoaderSelector = (state) => state.property.allRestaurantLoader;
export const allRestaurantSelector = (state) => state.property.allRestaurant;
export const allRestaurantErrorSelector = (state) => state.property.allRestaurantError;
export const allRestaurantChefSelector = (state) => state.property.allRestaurantChef;
export const allRestaurantChefErrorSelector = (state) =>
  state.property.allRestaurantChefErrorSelector;
export const allSpaInfoSelector = (state) => state.property.allSpaInfo;
export const allSpaCategoriesSelector = (state) => state.property.allSpaCategory;
export const allSpaTreatmentsSelector = (state) => state.property.allSpaTreatments;
export const allUIBuilderPagesSelector = (state) => state.property.allUIBuilderPages;
export const allUIBuilderFlowsSelector = (state) => state.property.allUIBuilderFlows;
export const allUIBuilderHotelModuleOptionsSelector = (state) =>
  state.property.allUIBuilderHotelModuleOptions;
export const uiBuilderMenuListSelector = (state) => state.property.uiBuilderMenu;
export const uiBuilderMenuLoaderSelector = (state) => state.property.uiBuilderMenuLoader;
export const uiBuilderHamburgerMenuListSelector = (state) => state.property.uiBuilderHamburgerMenu;
export const dashboardSelectedSpaSelector = (state) => state.property.dashboardSelectedSpa;
export const dashboardSelectedRestaurantSelector = (state) =>
  state.property.dashboardSelectedRestaurant;
export const menuConfigSelectedRestaurantSelector = (state) =>
  state.property.menuConfigSelectedRestaurant;
export const allPropertiesDataSelector = (state) => state.property.allProperties;
export const paginationSelector = (state) => state.property.Pagination;
export const userInviteSelectedRestaurantSelector = (state) =>
  state.property.userInviteSelectedRestaurant;
export const uiBuilderEditModeStatueSelector = (state) => state.property.isUiBuilderEditMode;

export const allPropertyConfigsSelector = (state) => state.property.allPropertyConfigs;
export const getActiveGroup = (state) => state.property.activeGroup;
export default propertySlice.reducer;

export const selectPrimaryCuisineDetails = (state) => state.property.primaryCuisineDetails;

export const selectSecondaryCuisineDetails = (state) => state.property.secondaryCuisineDetails;
